import { useEffect, useState } from "react";

const CONSENT_EVENTS = [
  "CookiebotOnConsentReady",
  "CookiebotOnAccept",
  "CookiebotOnDecline",
] as const;

// Never block the consumer forever if Cookiebot fails to load (script blocked,
// ad-blocker, etc.) – fall back to resolving after this delay.
const COOKIEBOT_FALLBACK_MS = 12000;

type CookiebotWindow = Window & {
  Cookiebot?: { consent?: { method?: "explicit" | null } };
};

const hasExplicitResponse = (): boolean =>
  typeof window !== "undefined" &&
  (window as CookiebotWindow).Cookiebot?.consent?.method === "explicit";

/**
 * Resolves once the user has responded to the Cookiebot banner (or when
 * consent already exists from a previous visit). Useful for deferring UI that
 * would otherwise overlap or block the cookie banner.
 */
export const useCookiebotSettled = (enabled: boolean): boolean => {
  const [responded, setResponded] = useState(() => hasExplicitResponse());

  useEffect(() => {
    if (!enabled || responded || typeof window === "undefined") {
      return;
    }

    if (hasExplicitResponse()) {
      setResponded(true);
      return;
    }

    const onResolved = () => setResponded(true);
    CONSENT_EVENTS.forEach((event) =>
      window.addEventListener(event, onResolved),
    );

    const fallback = window.setTimeout(
      () => setResponded(true),
      COOKIEBOT_FALLBACK_MS,
    );

    return () => {
      CONSENT_EVENTS.forEach((event) =>
        window.removeEventListener(event, onResolved),
      );
      window.clearTimeout(fallback);
    };
  }, [enabled, responded]);

  return responded;
};
